<template>
    <div :class="{ 'rule-editor-container': index > 0 }">
        <fieldset
            class="rule-editor"
            :class="{
                'rule-editor--with-or-label': index > 0,
                'rule-editor--with-event-property': rule.useEventProperty,
            }">
            <legend class="visually-hidden">
                {{ $t('ruleEditor.Segment OR Rule {ruleNumber} in group {groupNumber}', { ruleNumber: ruleNumber, groupNumber: groupNumber }) }}
            </legend>
            <div class="rule-editor-section">
                <div
                    v-if="index > 0"
                    class="rule-editor__or-label">
                    {{ $t('ruleEditor.OR') }}
                </div>
                <div
                    data-cy="rule-editor"
                    class="rule-editor__controls">
                    <template v-if="selectedGroup && !isLocaleEnglish">
                        <div class="flex-break" />
                        <strong>{{ $t('Data source') }}</strong>
                        <div class="flex-break" />
                    </template>
                    <pendo-multiselect
                        class="group-select"
                        :value="selectedGroup"
                        :disabled="readOnly"
                        :options="groupOptions"
                        :append-to-body="false"
                        :extra-label-width="60"
                        :sort-method="sortGroupSelectOptions"
                        :value-key="valueKey"
                        label-key="name"
                        min-trigger-width="80px"
                        :placeholder="$t('ruleEditor.Filter by...')"
                        :aria-label="filterByAriaLabel"
                        @select="onGroupOptionSelect">
                        <template #option="{ option }">
                            <pendo-icon-option
                                v-pendo-tooltip="option.tooltip"
                                :option="option" />
                        </template>
                        <template #selectedLabel="{ option }">
                            <pendo-icon-option
                                v-pendo-tooltip="option.tooltip"
                                :option="option" />
                        </template>
                    </pendo-multiselect>
                    <app-select
                        v-if="showAppSelector"
                        show-platform-group-options
                        preselect-first
                        :read-only="readOnly"
                        :apps="apps"
                        :rule="rule"
                        @input="onAppSelect" />
                    <component
                        :is="ruleComponent"
                        v-if="selectedGroup !== undefined"
                        :read-only="readOnly"
                        :check-valid-rule="checkValidRule"
                        :loading-resources="isLoadingResources"
                        :resources="resourcesForSelectedApp"
                        :rule="rule"
                        :config="config"
                        :is-multi-app="apps.length > 1"
                        :hide-groups="isPendoFree"
                        :segment="segment"
                        :is-locale-english="isLocaleEnglish"
                        :metadata="metadata"
                        :historical-metadata="historicalMetadata"
                        :session-properties="sessionProperties"
                        :include-page-parameter="includePageParameter"
                        :include-location-data="includeLocationData"
                        :include-server-data="includeServerData"
                        @input="change"
                        @bulk-input="bulkInput"
                        @time-rule-changed="shouldDisplayAlert" />
                </div>
            </div>
            <pendo-button
                v-if="!readOnly"
                class="rule-editor-section__close-button"
                theme="app"
                type="tertiary"
                :aria-label="removeRuleAriaLabel"
                @click="removeOrRule">
                <div v-pendo-tooltip="{ content: $t('ruleEditor.Remove'), delay: { show: 500, hide: 0 } }">
                    <pendo-icon
                        aria-hidden="true"
                        type="x"
                        display="grid"
                        :stroke-width="2.5"
                        :size="18" />
                </div>
            </pendo-button>
        </fieldset>
        <div v-if="displayAlert">
            <pendo-alert
                closable
                title=""
                :description="
                    $t(
                        'ruleEditor.This option only looks at the last response a user submits. If a user first submits a \'Yes\' and then a \'No\', it counts as a \'No\' in the selected time frame.',
                    )
                " />
        </div>
    </div>
</template>

<script>
import { PendoAlert, PendoButton, PendoIcon, PendoTooltip, PendoMultiselect, PendoIconOption } from '@pendo/components';
import isPlainObject from 'lodash/isPlainObject';
import get from 'lodash/get';

import { mapActions, mapGetters, mapState } from 'vuex';

import SessionPropertyRule from './session-property-rule.vue';
import GuideRule from '@/segments/components/segment-editor-vue/guide-rule.vue';
import GuideExperimentRule from '@/segments/components/segment-editor-vue/guide-experiment-rule.vue';
import GuideElementRule from '@/segments/components/segment-editor-vue/guide-element-rule.vue';
import ResourceCenterRule from '@/segments/components/segment-editor-vue/resource-center-rule.vue';
import EmailRule from '@/segments/components/segment-editor-vue/email-rule.vue';
import EmailElementRule from '@/segments/components/segment-editor-vue/email-element-rule.vue';
import AggregationRule from '@/segments/components/segment-editor-vue/aggregation-rule.vue';
import PollRule from '@/segments/components/segment-editor-vue/poll-rule.vue';
import MetadataRule from '@/segments/components/segment-editor-vue/metadata-rule.vue';
import PageFeatureTrackTypeRule from '@/segments/components/segment-editor-vue/page-feature-track-type-rule.vue';
import FeedbackRequestRule from '@/segments/components/segment-editor-vue/feedback-request-rule.vue';
import NestedSegmentRule from '@/segments/components/segment-editor-vue/nested-segment-rule.vue';
import IdeaRule from '@/segments/components/segment-editor-vue/idea-rule.vue';
import ApplicationRule from '@/segments/components/segment-editor-vue/application-rule.vue';
import WorkflowRule from '@/segments/components/segment-editor-vue/workflow-rule.vue';
import JourneyRule from '@/segments/components/segment-editor-vue/journey-rule.vue';
import SurveyRule from '@/segments/components/segment-editor-vue/survey-rule.vue';
import { FRUSTRATION_TYPES } from '@/session-replay/helpers/common.util.js';
import AppSelect from '@/segments/components/segment-editor-vue/components/app-select.vue';

import { groupOptionsByKind } from '@/segments/components/segment-editor-vue/segment-editor.utils.js';
import {
    SEGMENT_RULE_LIMIT,
    getIdsWithResources,
    APP_SPECIFIC_AUTO_METADATA_IDS,
    getAppLevelEventRules,
} from '@/utils/segments.js';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';

import { APP_PLATFORM_LIST } from '@/utils/apps';
import { i18n_locale } from '@/utils/i18n';

const getTooltip = (content) => ({
    content,
    delay: { show: 500, hide: 0 },
    placement: 'top',
    arrow: true,
    multiline: true,
});
const getText = (reason) => `${reason} cannot contain nested segment rules`;

export const NESTED_SEGMENT_DISABLED_TOOLTIPS = Object.freeze({
    IS_NESTED: getText('Segments that are already nested'),
    ONLY_ME: getText('Segments with visibility of only me'),
    NESTED_LIMIT: 'You’ve reached the limit of segments you can use.',
});

export default {
    name: 'RuleEditor',
    components: {
        AggregationRule,
        ApplicationRule,
        AppSelect,
        FeedbackRequestRule,
        GuideRule,
        GuideExperimentRule,
        GuideElementRule,
        ResourceCenterRule,
        EmailRule,
        MetadataRule,
        NestedSegmentRule,
        PageFeatureTrackTypeRule,
        PendoAlert,
        PendoButton,
        PendoIcon,
        PendoIconOption,
        PendoMultiselect,
        PollRule,
        SessionPropertyRule,
        SurveyRule,
        IdeaRule,
        WorkflowRule,
        JourneyRule,
        EmailElementRule,
    },
    directives: {
        PendoTooltip,
    },
    inject: ['segmentsUsedCount'],
    props: {
        rule: {
            type: Object,
            required: true,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        config: {
            type: Object,
            required: true,
        },
        index: {
            type: Number,
            default: -1,
        },
        ruleNumber: {
            type: Number,
            default: 1,
        },
        groupNumber: {
            type: Number,
            default: 1,
        },
        readOnly: {
            type: Boolean,
            required: true,
        },
        segment: {
            type: Object,
            required: true,
        },
        segmentIsNested: {
            type: Boolean,
            default: false,
        },
        hasDependentSegment: {
            type: Boolean,
            default: false,
        },
        isDraftEditor: {
            type: Boolean,
            required: false,
        },
        canEditAndShare: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['remove', 'input'],
    data () {
        return {
            appAwareIds: [
                'page',
                'feature',
                'trackType',
                'guide',
                'guideExperiment',
                'guideElement',
                'email',
                'emailElement',
                'journey',
                'poll',
                'deviceType',
                'sdkVersion',
                'appVersion',
                'osVersion',
                'deviceModel',
                'feedbackRequest',
                'journey',
                'survey',
                'utm',
                ...APP_SPECIFIC_AUTO_METADATA_IDS,
                ...getAppLevelEventRules(),
            ],
            isLoadingResources: false,
            displayAlert: false,
        };
    },
    computed: {
        ...mapState({
            ideas: (state) => state.vocItems.allIdeas,
            metadata: (state) => state.metadata.map,
        }),
        ...mapGetters({
            historicalMetadata: 'metadata/historicalMetadata',
            sessionProperties: 'metadata/sessionProperties',
        }),
        ...mapGetters({
            features: 'features/list',
            guides: 'guides/list',
            guideExperiments: 'experiments/getAllExperiments',
            pages: 'pages/list',
            trackTypes: 'trackTypes/list',
            feedbackRequests: 'feedbackRequests/list',
            segments: 'segments/list',
            workflows: 'workflows/list',
            journeys: 'journeys/list',
            emails: 'emails/list',
            surveysMap: 'surveys/list',
        }),
        ...mapGetters({
            apps: 'apps/allWebPlusIntegratedMobileApps',
            webApps: 'apps/integratedWebList',
            extensionApps: 'apps/integratedExtensionList',
            mobileApps: 'apps/integratedMobileList',
        }),
        ...mapGetters({
            getFeatureFlag: 'subscription/getFeatureFlag',
            isPendoFree: 'subscription/isPendoFree',
            hasAccountLevelAnalyticsEnabled: 'subscription/hasAccountLevelAnalyticsEnabled',
        }),
        ...mapGetters({
            hasSegmentFlag: 'auth/hasSegmentFlag',
            userCanAccessClassicFeedback: 'auth/userCanAccessClassicFeedback',
        }),
        isLocaleEnglish () {
            return i18n_locale?.value ? i18n_locale.value === 'en' : true;
        },
        utms () {
            const sessionPropertyName = this.rule?.name;
            const property = this.config.sessionProperties.find((prop) => prop.DisplayName === sessionPropertyName);

            return property.uniqueValues.map((str) => JSON.parse(str));
        },
        isAppLevelRule () {
            return getAppLevelEventRules().includes(this.rule.id);
        },
        isSessionPropertyRule () {
            return this.rule.id === 'utm';
        },
        groupOptions () {
            const groupOptions = groupOptionsByKind(this.filteredGroupOptions);
            if (get(this.segment, 'isFocusGroup')) {
                return groupOptions.filter((group) => group.id === 'account');
            }

            return groupOptions;
        },
        surveys () {
            return Object.values(this.surveysMap);
        },
        valueKey () {
            return this.isSessionPropertyRule ? 'name' : 'id';
        },
        selectedGroup () {
            if (this.isSessionPropertyRule) {
                return this.config.rules.find((rule) => rule.field === this.rule.field);
            }

            return this.config.rules.find((rule) => rule.id === this.rule.id);
        },
        isWorkflowProgressRuleEnabled () {
            const hasSegmentFlag = this.hasSegmentFlag('segmentByWorkflow');

            return hasSegmentFlag;
        },
        filteredGroupOptions () {
            const excludedIds = [];
            const isAEU = this.$productLine(PRODUCT_LINES.ADOPT);
            if (!this.userCanAccessClassicFeedback) {
                excludedIds.push('feedbackRequest');
            }

            if (!this.isVocProductlinePurchased) {
                excludedIds.push('idea');
            }

            if (!this.isSurveysProductlinePurchased) {
                excludedIds.push('survey');
            }

            if (!this.isWorkflowProgressRuleEnabled) {
                excludedIds.push('workflow');
            }

            if (!this.isOrchestrateProductlinePurchased) {
                excludedIds.push('journey');
            }

            if (!this.isEmailStandAlonePurchased) {
                excludedIds.push('email');
                excludedIds.push('emailElement');
            }

            if (!this.$productLine('guides', 'experimentsEnabled')) {
                excludedIds.push('guideExperiment');
            }

            const { IS_NESTED, ONLY_ME, NESTED_LIMIT } = NESTED_SEGMENT_DISABLED_TOOLTIPS;

            const filteredRules = this.config.rules
                .filter((rule) => {
                    const isAccountRule = ['account', 'parentAccount'].includes(rule.kind) ||
                        ['visitor_auto_accountids', 'visitor_agent_parentaccount'].includes(rule.id);
                    const filterAccountRule = !this.showAccounts && isAccountRule;
                    const hideFrustrationTypes = isAEU && FRUSTRATION_TYPES.some((frustrationType) => rule.id.includes(frustrationType));

                    return !excludedIds.includes(rule.id) && !filterAccountRule && !hideFrustrationTypes;
                })
                .map((rule) => {
                    const disableWithTooltip = (content) => ({
                        ...rule,
                        disabled: true,
                        tooltip: getTooltip(content),
                    });
                    if (rule.id === 'segment') {
                        if (this.segmentIsNested || this.hasDependentSegment) {
                            return disableWithTooltip(IS_NESTED);
                        }
                        if (this.segmentsUsedCount() >= SEGMENT_RULE_LIMIT) return disableWithTooltip(NESTED_LIMIT);
                        if (!this.isDraftEditor && (this.readOnly || !this.canEditAndShare)) {
                            return disableWithTooltip(ONLY_ME);
                        }
                    }

                    return rule;
                });

            return filteredRules;
        },
        showAppSelector () {
            if (this.apps.length > 1) {
                if (this.isAppLevelRule) {
                    return true;
                }

                return this.appAwareIds.includes(this.rule.id);
            }

            return false;
        },
        appIdsForSelectedApp () {
            let apps;
            // fallback to '*' in the case of single app
            const appId = this.rule.appId || '*';

            switch (appId) {
                case '*':
                    apps = this.apps;
                    break;
                case 'web':
                    apps = this.webApps;
                    break;
                case 'extension':
                    apps = this.extensionApps;
                    break;
                case 'mobile':
                    apps = this.mobileApps;
                    break;
                default:
                    apps = [{ id: this.rule.appId }];
                    break;
            }

            return apps.map((app) => app.id);
        },
        resourcesForId () {
            let resource = `${this.rule.id}s`;

            if (['guide', 'poll', 'guideElement', 'resourceCenter'].includes(this.rule.id)) {
                resource = 'guides';
            }

            if (['email', 'emailElement'].includes(this.rule.id)) {
                resource = 'emails';
            }

            if (resource === 'guides' && this.guides && this.guides.length === 0) {
                this.fetchResources('guide');
            }

            if (this.rule.id === 'application') {
                resource = 'apps';
            }

            return this[resource];
        },
        resourcesForSelectedApp () {
            if (!this.resourcesForId || this.isAppLevelRule) {
                return;
            }

            if (this.appAwareIds.includes(this.rule.id) && !this.isSessionPropertyRule) {
                return this.resourcesForId.filter((resource) => {
                    if (Object.hasOwn(resource, 'appId')) {
                        return this.appIdsForSelectedApp.includes(resource.appId);
                    }

                    return this.appIdsForSelectedApp.includes(resource.appIds?.[0]);
                });
            }

            return this.resourcesForId;
        },
        ruleComponent () {
            if (this.rule.id === 'application') {
                return 'application-rule';
            }

            if (this.rule.id === 'workflow') {
                return 'workflow-rule';
            }

            if (this.rule.id === 'segment') {
                return 'nested-segment-rule';
            }

            if (this.rule.id === 'poll') {
                return 'poll-rule';
            }

            if (this.rule.id === 'guide') {
                return 'guide-rule';
            }

            if (this.rule.id === 'guideExperiment') {
                return 'guide-experiment-rule';
            }

            if (this.rule.id === 'email') {
                return 'email-rule';
            }

            if (this.rule.id === 'feedbackRequest') {
                return 'feedback-request-rule';
            }

            if (this.rule.id === 'idea') {
                return 'idea-rule';
            }

            if (this.rule.id === 'survey') {
                return 'survey-rule';
            }

            if (this.rule.id === 'guideElement') {
                return 'guide-element-rule';
            }

            if (this.rule.id === 'resourceCenter') {
                return 'resource-center-rule';
            }

            if (['page', 'feature', 'trackType'].includes(this.rule.id)) {
                return 'page-feature-track-type-rule';
            }

            if (this.rule.id === 'emailElement') {
                return 'email-element-rule';
            }

            if (this.rule.schema === 'aggregation') {
                return this.isSessionPropertyRule ? 'session-property-rule' : 'aggregation-rule';
            }

            if (this.rule.id === 'journey') {
                return 'journey-rule';
            }

            return 'metadata-rule';
        },
        isCustomSegment () {
            return this.segment?.id === 'custom';
        },
        isVocProductlinePurchased () {
            return this.$productLine('voc', 'ideas');
        },
        isSurveysProductlinePurchased () {
            return this.$productLine('surveys');
        },
        isOrchestrateProductlinePurchased () {
            return this.$productLine('orchestrate');
        },
        isEmailStandAlonePurchased () {
            return this.$productLine('orchestrate', 'emailStandAloneEnabled');
        },
        hasEnhancedGroupingFilteringSegmentFlag () {
            return this.hasSegmentFlag('dxEnhancedGroupingFiltering');
        },
        includePageParameter () {
            return this.hasEnhancedGroupingFilteringSegmentFlag;
        },
        includeLocationData () {
            return this.hasEnhancedGroupingFilteringSegmentFlag && !this.getFeatureFlag('blockLogRemoteAddress');
        },
        includeServerData () {
            return this.hasEnhancedGroupingFilteringSegmentFlag;
        },
        showAccounts () {
            return this.hasAccountLevelAnalyticsEnabled && this.$productLine(PRODUCT_LINES.CORE, 'accountsUIEnabled');
        },
        filterByAriaLabel () {
            return this.$t('ruleEditor.Select filter type for rule {ruleNumber} in group {groupNumber}', {
                ruleNumber: this.ruleNumber,
                groupNumber: this.groupNumber,
            });
        },
        removeRuleAriaLabel () {
            return this.$t('ruleEditor.Remove rule {ruleNumber} from group {groupNumber}', {
                ruleNumber: this.ruleNumber,
                groupNumber: this.groupNumber,
            });
        },
    },
    created () {
        const idsWithResources = getIdsWithResources();
        if (idsWithResources.includes(this.rule.id)) {
            this.fetchResources(this.rule.id);
        }
    },
    methods: {
        ...mapActions({
            loadAllPages: 'pages/loadAll',
            loadAllFeatures: 'features/loadAll',
            loadAllTrackTypes: 'trackTypes/loadAll',
            loadAllGuides: 'guides/loadAll',
            loadAllExperiments: 'experiments/fetchAllExperiments',
            loadAllEmails: 'emails/loadAll',
            loadAllCoreEvents: 'coreEvents/loadAll',
            loadAllFeedbackRequests: 'feedbackRequests/loadAll',
            loadAllSegments: 'segments/loadAll',
            loadAllIdeas: 'vocItems/loadAll',
            loadAllWorkflows: 'workflows/loadAll',
            loadAllJourneys: 'journeys/loadAllJourneys',
            loadAllSurveys: 'surveys/loadAll',
        }),
        onAppSelect ({ value }) {
            const updates = {
                appId: value,
            };

            if (this.appAwareIds.includes(this.rule.id)) {
                const field = `${this.rule.id}Id`;

                let appIds;
                if (APP_PLATFORM_LIST.includes(value)) {
                    const apps = {
                        '*': this.apps,
                        'web': this.webApps,
                        'extension': this.extensionApps,
                        'mobile': this.mobileApps,
                    }[value];
                    appIds = apps.map((app) => app.id);
                } else {
                    appIds = [value];
                }

                // selecting an app group, need to check if rule has a
                // guideId, pageId, trackTypeId, featureId and if that
                // resource is available in the new app
                const resource = (this.resourcesForId || []).find(
                    (resource) => appIds.includes(resource.appId) && resource.id === this.rule[field],
                );

                if (!resource) {
                    updates[field] = null;
                    updates.useEventProperty = false;
                }
            }

            this.$emit('input', updates);
        },
        async onGroupOptionSelect (option) {
            const { icon, ...updates } = option;

            // preserve appId
            if (this.rule.appId && this.appAwareIds.includes(updates.id)) {
                updates.appId = this.rule.appId;
            }

            // Initialize appId to '*' for mobile version rules in multi-app environments
            // This prevents the aggregation from using the default app ID (__323232)
            // which causes incorrect field naming (userAgent__323232 vs userAgent)
            if (
                !this.rule.appId &&
                (this.appAwareIds.includes(updates.id) || this.isAppLevelRule) &&
                this.apps.length > 1
            ) {
                updates.appId = '*';
            }

            const aggBasedRuleIds = ['page', 'feature', 'trackType', 'utm'];
            if (
                !aggBasedRuleIds.includes(this.rule.id) &&
                !aggBasedRuleIds.includes(updates.id) &&
                updates.schema === 'aggregation'
            ) {
                updates.time = this.rule.time;
                if (updates.time === 'withinLast') {
                    updates.granularity = this.rule.granularity;
                    updates.count = this.rule.count;
                    updates.value = this.rule.value;
                }

                if (updates.time === 'between') {
                    updates.first = this.rule.first;
                    updates.last = this.rule.last;
                }
            }

            this.$emit('input', updates);
            const idsWithResources = getIdsWithResources();
            if (idsWithResources.includes(updates.id)) {
                this.fetchResources(updates.id);
            }
        },
        change ({ field, value }) {
            const updates = {};

            if (Array.isArray(field)) {
                field.forEach((key) => {
                    updates[key] = value[key];
                });
            } else if (isPlainObject(value)) {
                updates[field] = value[field];
            } else {
                updates[field] = value;
            }

            this.$emit('input', updates);
        },
        bulkInput (updates) {
            this.$emit('input', updates);
        },
        async fetchResources (id) {
            this.isLoadingResources = true;
            await this.$nextTick();

            const requestForId = {
                page: () => Promise.all([this.loadAllPages(), this.loadAllCoreEvents()]),
                feature: () => Promise.all([this.loadAllFeatures(), this.loadAllCoreEvents()]),
                trackType: () => Promise.all([this.loadAllTrackTypes(), this.loadAllCoreEvents()]),
                guide: this.loadAllGuides,
                guideExperiment: this.loadAllExperiments,
                guideElement: this.loadAllGuides,
                email: this.loadAllEmails,
                resourceCenter: this.loadAllGuides,
                poll: this.loadAllGuides,
                feedbackRequest: this.loadAllFeedbackRequests,
                segment: this.loadAllSegments,
                idea: this.loadAllIdeas,
                workflow: () => Promise.all([this.loadAllWorkflows(), this.loadAllPages(), this.loadAllFeatures()]),
                journey: () => Promise.all([this.loadAllJourneys()]),
                survey: this.loadAllSurveys,
            }[id];

            await requestForId();
            this.isLoadingResources = false;
        },
        sortGroupSelectOptions (a, b) {
            if (a.id === 'utm' || b.id === 'utm') {
                return 0;
            }
            if (a.id === 'visitor_auto_firstvisit') {
                return -1;
            }
            if (b.id === 'visitor_auto_firstvisit') {
                return 1;
            }

            return a.name?.localeCompare(b.name);
        },
        removeOrRule () {
            this.$emit('remove');
            this.displayAlert = false;
        },
        shouldDisplayAlert (timeRule) {
            this.displayAlert = timeRule.time === 'ever';
        },
    },
};
</script>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.tooltip.pendo-tooltip {
    z-index: 30000;
}
</style>

<style scoped lang="less">
@media (min-width: 530px) {
    .rule-editor {
        user-select: none;
        min-width: 0;
        display: grid;
        height: 100%;
        grid-template-columns: 1fr 48px;
    }
}

@media (max-width: 529px) {
    .rule-editor {
        min-width: 0;
        display: flex;
        flex-wrap: wrap-reverse;
    }
}
.rule-editor {
    .rule-editor-section {
        display: contents;
    }

    .rule-editor__controls {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: 8px 16px;
        margin: 0 16px;
        padding: 16px 0;
        min-width: 0;
    }

    .rule-editor__or-label {
        grid-row: 1;
        margin: 1em 16px;
        align-self: center;
        justify-content: start;
        font-weight: 600;
        color: var(--palette-gray-70);
    }

    .rule-editor-section__close-button {
        grid-column: 2;
        transition: color 0.2s;
        color: var(--palette-gray-70);
        height: 48px;
        width: 48px;

        &:hover {
            color: var(--palette-gray-100);
        }
    }

    &.rule-editor--with-or-label {
        .rule-editor-section:nth-child(2) {
            .rule-editor__controls {
                grid-row: 2;
                padding: 0 0 16px;
            }
        }
    }

    &:last-child {
        border-bottom: 0;
    }

    .application-rule,
    .workflow-rule,
    .guide-rule,
    .guide-element-rule,
    .email-element-rule,
    .resource-center-rule,
    .poll-rule,
    .page-feature-track-type-rule,
    .aggregation-rule,
    .event-property-rule,
    .metadata-rule,
    .session-property-rule,
    .idea-rule,
    .journey-rule,
    .email-rule,
    .survey-rule {
        display: contents;
    }
}

:deep(.pendo-alert) {
    margin: 0px 16px 16px;
    width: auto;
}

.rule-editor-container {
    border-top: 1px solid var(--palette-gray-40);
}

.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
.visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
</style>
